import { PRODUCT_BOX_STYLE_TYPE } from 'b2b-components/theme/components/ProductList';
import { ProductAccessoryType } from 'b2b-apps/core/product/redux/productAccessories/types';
import { ProductFullType } from 'b2b-apps/core/product/redux/products/full/types';
import { ProductTileType } from 'b2b-apps/core/product/redux/products/tile/types';
import {
  ANALYZE_SEARCH_EVENT,
  ANALYZE_SEARCH_FILTER_CHANGE,
  ANALYZE_SEARCH_FILTER_RESULTS,
  ANALYZE_SEARCH_PAGE_VIEW,
  ANALYZE_SEARCH_RESULT_PRODUCT_CLICK,
  ANALYZE_SEARCH_RESULT_PRODUCT_INSERT_EVENT,
  ANALYZE_SIMILAR_SEARCH_QUERY,
  ANALYZE_SUGGESTED_CATEGORY_SEARCH,
  ANALYZE_SUGGESTED_SEARCH_RESULT_PRODUCT_CLICK,
} from 'b2b-apps/shop/analytics/redux';
import {
  AnalyticsProductType, TEALIUM_INTERACTION_ID,
  TEALIUM_PAGE_TYPE,
  TEALIUM_PROD_ACTION, TEALIUM_PROD_LIST,
} from 'b2b-apps/shop/analytics/types/types';
import { ProductType, WithPosition } from 'b2b-apps/shop/catalog/redux/suggestions/types';

enum SEARCH_COMPONENT_CATEGORY {
  srchFilter = 'srch_filter'
}

enum SEARCH_ACTION {
  select = 'select',
  deselect = 'deselect'
}

enum OSS_INTERACTION {
  start = 'start',
  view = 'view',
  click = 'click',
  other = 'other',
  filter = 'filter',
}

enum OSS_INTERACTION_LABEL {
  click = 'std_srch:start:via_button_or_enter',
  viewResultPage = 'std_srch:view:results_page',
  productClick = 'std_srch:click:prod',
  suggestionStart = 'sug_srch:start',
  suggestionClick = 'sug_srch:click:prod',
  suggestionStartFromSimmilars = 'std_srch:start:from_similar_query',
  suggestionCategory = 'std_srch:start:from_suggested_category',
  filter = 'std_srch:filter' // remember to add :filterName after
}

const OSS_INTERACTION_PARAMETERS: Partial<Record<TEALIUM_INTERACTION_ID,
  { interaction: OSS_INTERACTION, interactionLabel: OSS_INTERACTION_LABEL }
>> = {
  [TEALIUM_INTERACTION_ID.searchStart]: {
    interaction: OSS_INTERACTION.start,
    interactionLabel: OSS_INTERACTION_LABEL.click,
  },
  [TEALIUM_INTERACTION_ID.searchPageView]: {
    interaction: OSS_INTERACTION.view,
    interactionLabel: OSS_INTERACTION_LABEL.viewResultPage,
  },
  [TEALIUM_INTERACTION_ID.productSearchListClick]: {
    interaction: OSS_INTERACTION.click,
    interactionLabel: OSS_INTERACTION_LABEL.productClick,
  },
  [TEALIUM_INTERACTION_ID.productSearchAddToCart]: {
    interaction: OSS_INTERACTION.click,
    interactionLabel: OSS_INTERACTION_LABEL.productClick,
  },
  [TEALIUM_INTERACTION_ID.suggestedProductClick]: {
    interaction: OSS_INTERACTION.click,
    interactionLabel: OSS_INTERACTION_LABEL.suggestionClick,
  },
  [TEALIUM_INTERACTION_ID.similarQueryClick]: {
    interaction: OSS_INTERACTION.start,
    interactionLabel: OSS_INTERACTION_LABEL.suggestionStartFromSimmilars,
  },
  [TEALIUM_INTERACTION_ID.suggestedCategoriesSearchStart]: {
    interaction: OSS_INTERACTION.start,
    interactionLabel: OSS_INTERACTION_LABEL.suggestionCategory,
  },
  [TEALIUM_INTERACTION_ID.searchFilterClick]: {
    interaction: OSS_INTERACTION.filter,
    interactionLabel: OSS_INTERACTION_LABEL.filter,
  },
};

type SearchAnalyticsOssData = {
  interaction: OSS_INTERACTION;
  interactionLabel: OSS_INTERACTION_LABEL;
  searchPasses?: string;
  resultsPerPage?: string;
  pageIndex?: string;
  listType?: PRODUCT_BOX_STYLE_TYPE;
  resultsCount?: string;
  term: string;
  type: TEALIUM_PROD_LIST;
  originalQuery: string;
  suggestionSource?: string;
}

type SearchAnalyticsData = {
  interaction: TEALIUM_INTERACTION_ID;
  prod?: AnalyticsProductType[],
  page?: {
    type: TEALIUM_PAGE_TYPE;
  },
  oss: SearchAnalyticsOssData,
}

type SearchOssData = {
  query: string;
  originalQuery?: string;
  sortType?: string;
  resultsPerPage?: number;
  page?: number;
  productsCount?: number;
  listType?: PRODUCT_BOX_STYLE_TYPE;
}

type SearchData = SearchOssData & {
  id: TEALIUM_INTERACTION_ID;
  pageType?: TEALIUM_PAGE_TYPE;
  products?: (ProductFullType | ProductTileType | ProductAccessoryType)[];
  productsAction?: TEALIUM_PROD_ACTION;
  productsList?: TEALIUM_PROD_LIST;
  productQuantity?: number,
}

type AnalyzeSearchEventAction = {
  type: typeof ANALYZE_SEARCH_EVENT;
}

type AnalyzeSearchPageViewAction = {
  type: typeof ANALYZE_SEARCH_PAGE_VIEW;
};

type AnalyzeSearchResultProductClickAction = {
  type: typeof ANALYZE_SEARCH_RESULT_PRODUCT_CLICK;
  payload: {
    sku: string,
  }
};

type AnalyzeSearchResultProductInsertAction = {
  type: typeof ANALYZE_SEARCH_RESULT_PRODUCT_INSERT_EVENT;
  payload: {
    sku: string,
    actionType?: TEALIUM_PROD_ACTION,
    quantity: number,
  }
};

type AnalyzeSuggestedSearchResultProductClickAction = {
  type: typeof ANALYZE_SUGGESTED_SEARCH_RESULT_PRODUCT_CLICK;
  payload: {
    product: WithPosition<ProductType>,
  }
};

type AnalyzeSimilarSearchQueryAction = {
  type: typeof ANALYZE_SIMILAR_SEARCH_QUERY;
};

type AnalyzeSuggestedCategorySearchAction = {
  type: typeof ANALYZE_SUGGESTED_CATEGORY_SEARCH;
  payload: {
    facetName: string,
  }
};

type AnalyzeSearchFilterChangeAction = {
  type: typeof ANALYZE_SEARCH_FILTER_CHANGE;
  payload: {
    filterName: string;
    filterValue: string;
    filterAction: SEARCH_ACTION
  }
}

enum FILTER_TYPE {
  catalogAll = 'catalog_finder',
  inkTonerFilter = 'ink_finder'
}

type AnalyzeSearchFilterResultsAction = {
  type: typeof ANALYZE_SEARCH_FILTER_RESULTS;
  payload: {
    filterType: FILTER_TYPE,
    filters: Record<string, string | boolean>
  };
}

interface AnalyzeCatalogFilters {
  filterType: FILTER_TYPE,
  filters: Record<string, string | any>
}

type SearchAnalyticsAction =
  | AnalyzeSearchEventAction
  | AnalyzeSearchPageViewAction
  | AnalyzeSearchResultProductClickAction
  | AnalyzeSearchResultProductInsertAction
  | AnalyzeSuggestedSearchResultProductClickAction
  | AnalyzeSimilarSearchQueryAction
  | AnalyzeSuggestedCategorySearchAction
  | AnalyzeSearchFilterChangeAction;

export {
  OSS_INTERACTION,
  OSS_INTERACTION_LABEL,
  OSS_INTERACTION_PARAMETERS,
  SEARCH_ACTION,
  SEARCH_COMPONENT_CATEGORY,
};

export {
  FILTER_TYPE,
};

export type {
  AnalyzeCatalogFilters,
  AnalyzeSearchEventAction,
  AnalyzeSearchFilterChangeAction,
  AnalyzeSearchFilterResultsAction,
  AnalyzeSearchPageViewAction,
  AnalyzeSearchResultProductClickAction,
  AnalyzeSearchResultProductInsertAction,
  AnalyzeSimilarSearchQueryAction,
  AnalyzeSuggestedCategorySearchAction,
  AnalyzeSuggestedSearchResultProductClickAction,
  SearchAnalyticsAction,
  SearchAnalyticsData,
  SearchAnalyticsOssData,
  SearchData,
  SearchOssData,
};
